import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-hot-toast";
import AppIcon from "../../../components/ui/AppIcon";
import { useAuth } from "../../../context/AuthContext";
import { useChat } from "../../../context/ChatContext";
import { useSocket } from "../../../context/SocketContext";
import { storeChats, uploadFile } from "../../../services/chatServices";
import { sendChannelMessage } from "../../../services/channelService";
import { useDraft } from "../hooks/useDraft";
import DraftIndicator from "./DraftIndicator";

import EmojiPickerLib from "emoji-picker-react";

// ── Emoji Picker — portal-based so it escapes all overflow/stacking contexts ─
function EmojiPicker({ anchorRef, onSelect, onClose, pickerRef }) {
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        // Position above the button, right-aligned
        setCoords({
            top: rect.top + window.scrollY - 400, // 400 ≈ picker height + gap
            left: Math.max(8, rect.right + window.scrollX - 320), // 320 = picker width
        });
    }, [anchorRef]);

    return ReactDOM.createPortal(
        <div
            ref={pickerRef}
            style={{ position: "absolute", top: coords.top, left: coords.left, zIndex: 9999 }}
        >
            <EmojiPickerLib
                onEmojiClick={(emojiData) => {
                    onSelect(emojiData.emoji);
                    onClose();
                }}
                autoFocusSearch
                lazyLoadEmojis
                skinTonesDisabled
                height={380}
                width={320}
                searchPlaceholder="Search emoji..."
                previewConfig={{ showPreview: false }}
            />
        </div>,
        document.body
    );
}

// ── Link Modal ───────────────────────────────────────────────────────────────
function LinkModal({ selectedText, onInsert, onClose }) {
    const [url, setUrl] = useState("https://");
    const [text, setText] = useState(selectedText || "");

    const handleInsert = () => {
        if (!url.trim()) return;
        onInsert(`[${text || url}](${url})`);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 backdrop-blur-sm animate-fade-in">
            <div className="w-96 bg-app-surface border border-border-subtle rounded-3xl shadow-modal p-6">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-sm font-bold text-text-primary uppercase tracking-widest">Insert link</h3>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-black/5 text-text-muted hover:text-text-primary transition-all">
                        <AppIcon name="XMarkIcon" size={18} />
                    </button>
                </div>
                <div className="flex flex-col gap-4">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-text-muted block mb-1.5 opacity-60">Text to display</label>
                        <input
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Link text"
                            className="w-full bg-app-bg border border-border-subtle rounded-xl px-4 py-3 text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/5 transition-all shadow-subtle"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-text-muted block mb-1.5 opacity-60">Link URL</label>
                        <input
                            autoFocus
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleInsert(); if (e.key === "Escape") onClose(); }}
                            placeholder="https://example.com"
                            className="w-full bg-app-bg border border-border-subtle rounded-xl px-4 py-3 text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/5 transition-all shadow-subtle"
                        />
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onClose}
                            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-text-muted hover:text-text-primary hover:bg-bg-muted transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleInsert}
                            disabled={!url.trim() || url === "https://"}
                            className={`flex-1 py-3 rounded-xl text-[13px] font-bold shadow-premium transition-all ${url.trim() && url !== "https://"
                                ? "bg-primary text-white hover:bg-primary-dark shadow-primary/20"
                                : "bg-black/5 text-text-muted cursor-not-allowed opacity-50"
                                }`}
                        >
                            Add link
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Mention List Component ───────────────────────────────────────────────────
function MentionDropdown({ query, members = [], onSelect, dropdownRef }) {
    const filtered = members.filter((m) =>
        m.name?.toLowerCase().includes(query.toLowerCase()) ||
        m.username?.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 6);

    if (filtered.length === 0) return null;

    return (
        <div
            ref={dropdownRef}
            className="absolute bottom-full mb-2 left-0 z-50 w-64 rounded-2xl bg-app-surface border border-border-subtle shadow-modal overflow-hidden animate-fade-in"
        >
            <div className="px-3 py-2 border-b border-border-subtle">
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Mention someone</p>
            </div>
            {filtered.map((member, i) => (
                <button
                    key={member._id || member.id || i}
                    // onMouseDown + preventDefault keeps textarea focus intact,
                    // preventing the blur→onChange→re-parse race that causes duplication.
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onSelect(member);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-3 hover:bg-black/5 transition-colors text-left"
                >
                    <img
                        src={member.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || "U")}&background=random`}
                        className="w-9 h-9 rounded-xl object-cover border border-border-subtle shadow-subtle"
                        alt={member.name}
                    />
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-text-primary truncate">{member.name || member.displayName}</p>
                        {member.username && <p className="text-[11px] text-text-muted truncate">@{member.username}</p>}
                    </div>
                </button>
            ))}
        </div>
    );
}

// ── Main MessageInput ────────────────────────────────────────────────────────
export default function MessageInput({ channelName, onMessagesUpdate, channelMembers = [] }) {
    const { user } = useAuth();
    const { activeChat, updateChatDraftStatus, refetchDrafts } = useChat();
    const socket = useSocket();
    const userId = user?._id || user?.id;
    // Stable ref so refetchDrafts is never a useCallback/useEffect dep in this component
    const refetchDraftsRef = useRef(refetchDrafts);
    useEffect(() => { refetchDraftsRef.current = refetchDrafts; }, [refetchDrafts]);

    // Extract chatId and chatType from activeChat
    const chatId = activeChat?.data?._id || activeChat?.data?.id;
    const chatType = activeChat?.type;

    // Draft hook (auto-save, persistence)
    const {
        value,
        setValue: setDraftValue,
        saveDraft,
        clearDraft,
        lastSaved,
        hasUnsavedChanges,
        isLoading: draftLoading,
        scheduledAt,
        setScheduled,
        clearScheduled,
        suppressAutoSave,
    } = useDraft(chatId, chatType, "");

    const [isCode, setIsCode] = useState(false);
    const [showScheduler, setShowScheduler] = useState(false);
    const [scheduledDate, setScheduledDate] = useState("");
    const [scheduledTime, setScheduledTime] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const [focused, setFocused] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isScheduling, setIsScheduling] = useState(false);
    const [pendingFile, setPendingFile] = useState(null); // { url, name, size, type } preview before sending

    // Helper to get file icon based on extension
    const getFileIcon = (filename) => {
        const ext = filename.split('.').pop()?.toLowerCase();
        const iconMap = {
            pdf: 'DocumentTextIcon',
            doc: 'DocumentTextIcon',
            docx: 'DocumentTextIcon',
            xls: 'TableCellsIcon',
            xlsx: 'TableCellsIcon',
            zip: 'ArchiveBoxIcon',
            rar: 'ArchiveBoxIcon',
            jpg: 'PhotoIcon',
            jpeg: 'PhotoIcon',
            png: 'PhotoIcon',
            gif: 'PhotoIcon',
            webp: 'PhotoIcon',
            mp4: 'VideoCameraIcon',
            mov: 'VideoCameraIcon',
            webm: 'VideoCameraIcon',
            mp3: 'MusicalNoteIcon',
            default: 'DocumentIcon'
        };
        return iconMap[ext] || iconMap.default;
    };

    // Toolbar state
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [selectedText, setSelectedText] = useState("");
    const [mentionQuery, setMentionQuery] = useState(null); // null = hidden, string = query
    const [mentionStartIdx, setMentionStartIdx] = useState(-1);
    // Refs that mirror mention state — read synchronously inside event handlers
    // to avoid stale closure values from React's batched state updates.
    const mentionQueryRef = useRef(null);
    const mentionStartIdxRef = useRef(-1);
    // Snapshot of the textarea value at the moment the @ was typed.
    // handleMentionSelect reads this instead of the React state `value`
    // so the slice indices are always correct regardless of re-render timing.
    const mentionBaseValueRef = useRef("");
    // Guard: prevents handleMentionSelect from running twice if both
    // mousedown and click fire (touch devices, accessibility tools).
    const mentionSelectingRef = useRef(false);

    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const emojiBtnRef = useRef(null);
    const mentionDropdownRef = useRef(null);

    // ── Close emoji on outside click ──────────────────────────────────────
    useEffect(() => {
        if (!showEmojiPicker) return;
        const handler = (e) => {
            // Ignore if click is inside the picker OR on the toggle button itself
            if (emojiPickerRef.current && emojiPickerRef.current.contains(e.target)) return;
            if (emojiBtnRef.current && emojiBtnRef.current.contains(e.target)) return;
            setShowEmojiPicker(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showEmojiPicker]);

    // ── Cursor helpers ────────────────────────────────────────────────────
    const getSelectionInfo = () => {
        const el = inputRef.current;
        if (!el) return { start: 0, end: 0, selected: "" };
        return {
            start: el.selectionStart,
            end: el.selectionEnd,
            selected: value.slice(el.selectionStart, el.selectionEnd),
        };
    };

    const insertAtCursor = useCallback((before, after = "", replaceSelected = false) => {
        const el = inputRef.current;
        if (!el) return;
        const { start, end, selected } = getSelectionInfo();
        const inner = replaceSelected ? selected : selected;
        const newText =
            value.slice(0, start) +
            before +
            inner +
            after +
            value.slice(end);
        setDraftValue(newText);
        // Restore cursor after React re-render
        requestAnimationFrame(() => {
            el.focus();
            const cursor = start + before.length + inner.length + after.length;
            el.setSelectionRange(
                replaceSelected ? start + before.length : cursor,
                replaceSelected ? start + before.length + inner.length : cursor
            );
        });
    }, [value]);

    // ── Bold (B) ──────────────────────────────────────────────────────────
    const handleBold = () => {
        document.execCommand("bold");
    };

    // ── Italic (I) ────────────────────────────────────────────────────────
    const handleItalic = () => {
        document.execCommand("italic");
    };

    // ── Link ──────────────────────────────────────────────────────────────
    const handleLinkClick = () => {
        const { selected } = getSelectionInfo();
        setSelectedText(selected);
        setShowLinkModal(true);
    };

    const handleLinkInsert = (markdownLink) => {
        const el = inputRef.current;
        if (!el) return;
        const { start, end } = getSelectionInfo();
        const newText = value.slice(0, start) + markdownLink + value.slice(end);
        setDraftValue(newText);
        requestAnimationFrame(() => {
            el.focus();
            const cursor = start + markdownLink.length;
            el.setSelectionRange(cursor, cursor);
        });
    };

    // ── Bullet List ───────────────────────────────────────────────────────
    const handleBulletList = () => {
        const el = inputRef.current;
        if (!el) return;
        const { start, end } = getSelectionInfo();
        const beforeCursor = value.slice(0, start);
        const afterCursor = value.slice(end);

        // If selection spans multiple lines, bullet each line
        if (start !== end) {
            const selectedLines = value.slice(start, end).split("\n");
            const bulleted = selectedLines
                .map((line) => (line.startsWith("• ") ? line.slice(2) : `• ${line}`))
                .join("\n");
            const newText = beforeCursor + bulleted + afterCursor;
            setDraftValue(newText);
            requestAnimationFrame(() => {
                el.focus();
                el.setSelectionRange(start, start + bulleted.length);
            });
            return;
        }

        // Single line: find the start of the current line
        const lineStart = beforeCursor.lastIndexOf("\n") + 1;
        const currentLine = value.slice(lineStart, end);

        if (currentLine.startsWith("• ")) {
            // Remove bullet
            const newText = value.slice(0, lineStart) + currentLine.slice(2) + value.slice(end);
            setDraftValue(newText);
            requestAnimationFrame(() => {
                el.focus();
                el.setSelectionRange(start - 2, start - 2);
            });
        } else {
            // Add bullet at line start
            const newText = value.slice(0, lineStart) + "• " + value.slice(lineStart);
            setDraftValue(newText);
            requestAnimationFrame(() => {
                el.focus();
                el.setSelectionRange(start + 2, start + 2);
            });
        }
    };

    // Continue bullets on Enter
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            if (mentionQuery !== null) {
                e.preventDefault();
                return;
            }
            if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
                return;
            }
            // Shift+Enter: if current line is a bullet, auto-continue
            const el = inputRef.current;
            const beforeCursor = value.slice(0, el.selectionStart);
            const lineStart = beforeCursor.lastIndexOf("\n") + 1;
            const currentLine = value.slice(lineStart, el.selectionStart);
            if (currentLine.startsWith("• ")) {
                e.preventDefault();
                insertAtCursor("\n• ", "");
            }
        }

        // Escape closes mention
        if (e.key === "Escape" && mentionQuery !== null) {
            setMentionQuery(null);
        }
    };

    // ── Emoji ─────────────────────────────────────────────────────────────
    const handleEmojiSelect = (emoji) => {
        const el = inputRef.current;
        if (!el) return;
        const { start, end } = getSelectionInfo();
        const newText = value.slice(0, start) + emoji + value.slice(end);
        setDraftValue(newText);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + emoji.length, start + emoji.length);
        });
    };

    // ── @ Mention ─────────────────────────────────────────────────────────
    const handleAtClick = () => {
        const el = inputRef.current;
        if (!el) return;
        const { start } = getSelectionInfo();
        const currentVal = el.value; // read live DOM value
        const newText = currentVal.slice(0, start) + "@" + currentVal.slice(start);
        // Snapshot the base value before any mention query is typed
        mentionBaseValueRef.current = newText;
        mentionStartIdxRef.current = start + 1;
        mentionQueryRef.current = "";
        setMentionStartIdx(start + 1);
        setMentionQuery("");
        setDraftValue(newText);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + 1, start + 1);
        });
    };

    const handleMentionSelect = (member) => {
        // Guard: ignore duplicate calls (mousedown + click race on some browsers)
        if (mentionSelectingRef.current) return;
        mentionSelectingRef.current = true;

        const el = inputRef.current;
        if (!el) {
            mentionSelectingRef.current = false;
            return;
        }

        // Use the snapshotted base value + refs — never the stale React state closure
        const base = mentionBaseValueRef.current;
        const startIdx = mentionStartIdxRef.current;  // position right after "@"
        const query = mentionQueryRef.current ?? "";

        const mention = `@${member.username || member.name} `;
        // Reconstruct: everything before "@" + mention + everything after the typed query
        const newText = base.slice(0, startIdx - 1) + mention + base.slice(startIdx + query.length);

        // Clear all mention state synchronously before any re-render
        mentionQueryRef.current = null;
        mentionStartIdxRef.current = -1;
        mentionBaseValueRef.current = "";
        setMentionQuery(null);
        setMentionStartIdx(-1);
        setDraftValue(newText);

        const cursor = startIdx - 1 + mention.length;
        requestAnimationFrame(() => {
            mentionSelectingRef.current = false;
            el.focus();
            el.setSelectionRange(cursor, cursor);
        });
    };

    // Detect @ in textarea
    const handleChange = (e) => {
        const newVal = e.target.value;

        // While a mention selection is being committed, ignore all change events.
        // This blocks the spurious onChange that fires when the textarea regains
        // focus after the dropdown button's mousedown.
        if (mentionSelectingRef.current) return;

        setDraftValue(newVal);

        // Emit typing only when there's content (debounced via emitTyping)
        if (newVal.trim()) {
            emitTyping();
        }

        // Mention detection
        const cursor = e.target.selectionStart;
        const textUpToCursor = newVal.slice(0, cursor);
        const atMatch = textUpToCursor.match(/@(\w*)$/);
        if (atMatch) {
            const q = atMatch[1];
            const idx = cursor - q.length;
            // Keep base snapshot current as the user types the query
            mentionBaseValueRef.current = newVal;
            mentionQueryRef.current = q;
            mentionStartIdxRef.current = idx;
            setMentionQuery(q);
            setMentionStartIdx(idx);
        } else {
            mentionBaseValueRef.current = "";
            mentionQueryRef.current = null;
            mentionStartIdxRef.current = -1;
            setMentionQuery(null);
        }
    };

    // ── Handle Sending ────────────────────────────────────────────────────
    const handleSend = async (overrideValue = null, overrideType = null) => {
        // If there's a pending file, send it first (takes priority)
        if (pendingFile && activeChat && user) {
            const fileUrl = pendingFile.url;
            const timestamp = new Date().toISOString();
            const tempId = `temp_${Date.now()}`;

            // Optimistic UI update for file
            const optimisticMsg = {
                _id: tempId,
                __tempId: tempId,
                senderId: userId,
                sender: user.name,
                // ── FIX 3: include senderInfo so MessageBubble can resolve
                //    the avatar immediately (same shape as text messages) ──
                senderInfo: {
                    _id: userId,
                    name: user.name,
                    profilePhoto: user.profilePhoto || user.profileImage || null,
                    username: user.username || null,
                },
                senderAvatar: user.profilePhoto || user.profileImage || null,
                content: fileUrl,
                message: fileUrl,
                text: fileUrl,
                timestamp,
                type: "url",
                status: "sending",
            };
            onMessagesUpdate((prev) => [...prev, optimisticMsg]);
            setPendingFile(null);

            try {
                if (activeChat.type === "channel") {
                    const channelId = activeChat.data._id || activeChat.data.id;
                    const reqPayload = {
                        channel: channelId,
                        sender: userId,
                        text: fileUrl,
                        type: "url",
                        __tempId: tempId,
                    };
                    const response = await sendChannelMessage(channelId, reqPayload);

                } else {
                    const receiverId = activeChat.data._id || activeChat.data.id;
                    const receiverName = activeChat.data.name || activeChat.data.displayName;
                    const sortedIds = [userId, receiverId].sort();
                    const reqPayload = {
                        user1Id: sortedIds[0],
                        user2Id: sortedIds[1],
                        messages: [
                            {
                                // ── FIX 1: pass senderId so the aggregation $lookup
                                //    can resolve profilePhoto on both sides ──────────
                                senderId: userId,
                                sender: user.name,
                                receiver: receiverName,
                                type: "url",
                                message: fileUrl,
                                timestamp,
                                // ── FIX 2: pass __tempId inside messages[0] so the
                                //    controller can attach it to the socket payload
                                //    and ChatPanel can replace the optimistic message
                                //    instead of appending a duplicate ───────────────
                                __tempId: tempId,
                            }
                        ]
                    };
                    const response = await storeChats(reqPayload);
                }
            } catch (error) {
                console.error("Error sending file:", error);
                onMessagesUpdate((prev) =>
                    prev.map((m) =>
                        m.__tempId === tempId ? { ...m, status: "error" } : m
                    )
                );
            }
            // Do not proceed to send text message in this call
            return;
        }

        const messageToSend = overrideValue ?? value;
        const msgType = overrideType ?? (isCode ? "code" : "text");

        if (!messageToSend.trim() || !activeChat || !user) return;

        // ── Handle Scheduled Messages ─────────────────────────────────────────────
        // If a schedule time is set, save the draft with scheduledAt and clear input.
        // The backend scheduler will automatically send the message at the scheduled time.
        if (scheduledAt) {
            if (isScheduling) return;
            setIsScheduling(true);
            try {
                // Ensure the draft with schedule is saved to backend
                await saveDraft(messageToSend);

                // Refresh drafts list to update modal
                await refetchDraftsRef.current();

                // Show confirmation to user
                const scheduledDate = new Date(scheduledAt);
                toast.success(`Message scheduled for ${scheduledDate.toLocaleString()}`);

                // Clear input while preserving the scheduled draft on server
                suppressAutoSave(); // Prevent auto-save from deleting the draft
                setDraftValue("");
                setScheduled(null);
                setShowScheduler(false);
                // Return early - do NOT send immediately
            } catch (error) {
                console.error("[MessageInput] ❌ Error scheduling message:", error.message, { stack: error.stack });
                toast.error("Failed to schedule message. Please try again.");
                return;
            } finally {
                setIsScheduling(false);
            }
            return;
        }
        // ─────────────────────────────────────────────────────────────────────────────

        const timestamp = new Date().toISOString();
        const tempId = `temp_${Date.now()}`;

        const optimisticMsg = {
            _id: tempId,
            __tempId: tempId,
            senderId: userId,
            sender: user.name,
            senderAvatar: user.profilePhoto || user.profileImage || null,
            senderInfo: {
                _id: userId,
                name: user.name,
                profilePhoto: user.profilePhoto || user.profileImage || null,
                username: user.username || null,
            },
            content: messageToSend,
            message: messageToSend,
            timestamp,
            type: msgType,
            status: "sending",
        };

        // ── OPTIMISTIC UI: Instant Clear ───────────────────────────────────
        onMessagesUpdate((prev) => [...prev, optimisticMsg]);
        setDraftValue(""); // Clear input instantly
        setIsCode(false);
        setShowScheduler(false);
        setMentionQuery(null);
        // Stop typing indicator immediately on send
        clearTimeout(typingTimeoutRef.current);
        if (socket && activeChat && user) {
            const targetId = activeChat.data._id || activeChat.data.id;
            if (activeChat.type === "dm") {
                socket.emit("stop-typing", { senderId: userId, receiverId: targetId, userName: user.name });
            } else {
                socket.emit("channel-stop-typing", { channelId: targetId, senderId: userId, userName: user.name });
            }
        }
        // ───────────────────────────────────────────────────────────────────

        try {
            if (activeChat.type === "channel") {
                const channelId = activeChat.data._id || activeChat.data.id;
                const reqPayload = {
                    channel: channelId,
                    sender: userId,
                    text: messageToSend,
                    type: msgType,
                    __tempId: tempId,
                };
                const response = await sendChannelMessage(channelId, reqPayload);

            } else {
                const receiverId = activeChat.data._id || activeChat.data.id;
                const receiverName = activeChat.data.name || activeChat.data.displayName;
                const timestamp = new Date().toISOString();

                // Sort user IDs to create consistent uniqueId
                const sortedIds = [userId, receiverId].sort();
                const user1Id = sortedIds[0];
                const user2Id = sortedIds[1];

                const reqPayload = {
                    user1Id,
                    user2Id,
                    messages: [
                        {
                            senderId: userId,
                            sender: user.name,
                            receiver: receiverName,
                            type: msgType,
                            message: messageToSend,
                            timestamp,
                            __tempId: tempId,
                        }
                    ]
                };
                const response = await storeChats(reqPayload);

            }
            // Backend cleaning (localStorage and API)
            clearDraft();
        } catch (error) {
            console.error("Error sending message:", error);
            onMessagesUpdate((prev) =>
                prev.map((m) =>
                    m.__tempId === tempId ? { ...m, status: "error" } : m
                )
            );
        }
    };

    // ── Handle File Upload ────────────────────────────────────────────────
    const handleFileUpload = async (file) => {
        if (!file || !activeChat || !user) return;
        setIsUploading(true);
        // Capture the expected conversation identifier at upload start
        const expectedConversationId = activeChat.type === "channel"
            ? (activeChat.data._id || activeChat.data.id)
            : [user._id || user.id, activeChat.data._id || activeChat.data.id].sort().join("_");

        try {
            const formData = new FormData();
            formData.append("image", file);
            const response = await uploadFile(formData);
            if (response?.data) {
                // Only set pending file if the chat hasn't changed during upload
                const currentConversationId = activeChat.type === "channel"
                    ? (activeChat.data._id || activeChat.data.id)
                    : [user._id || user.id, activeChat.data._id || activeChat.data.id].sort().join("_");
                if (currentConversationId === expectedConversationId) {
                    setPendingFile({
                        url: response.data,
                        name: file.name,
                        size: file.size,
                        type: file.type
                    });
                } else {
                    // Chat changed, discard upload result
                    console.warn("Upload completed but chat changed; discarding file preview.");
                }
            }
        } catch (error) {
            console.error("File upload failed:", error);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleCancelFile = () => {
        setPendingFile(null);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) handleFileUpload(file);
    };

    // ── Typing Indicator ──────────────────────────────────────────────────
    // Called from handleChange — debounced so we don't spam on every keystroke
    const emitTyping = useCallback(() => {
        if (!socket || !activeChat || !user) return;
        const targetId = activeChat.data._id || activeChat.data.id;

        if (activeChat.type === "dm") {
            socket.emit("typing", { senderId: userId, receiverId: targetId, userName: user.name });
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
                socket.emit("stop-typing", { senderId: userId, receiverId: targetId, userName: user.name });
            }, 1500);
        } else {
            // channel — broadcast to the room (joinChannel already joined it)
            socket.emit("channel-typing", { channelId: targetId, senderId: userId, userName: user.name });
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
                socket.emit("channel-stop-typing", { channelId: targetId, senderId: userId, userName: user.name });
            }, 1500);
        }
    }, [socket, activeChat, user, userId]);

    // Stop typing immediately when chat changes or component unmounts
    useEffect(() => {
        return () => {
            clearTimeout(typingTimeoutRef.current);
            if (!socket || !activeChat || !user) return;
            const targetId = activeChat.data._id || activeChat.data.id;
            if (activeChat.type === "dm") {
                socket.emit("stop-typing", { senderId: userId, receiverId: targetId, userName: user.name });
            } else {
                socket.emit("channel-stop-typing", { channelId: targetId, senderId: userId, userName: user.name });
            }
        };
    }, [activeChat]); // eslint-disable-line react-hooks/exhaustive-deps

    // Propagate draft status to ChatContext for sidebar highlighting
    useEffect(() => {
        if (chatId && updateChatDraftStatus) {
            updateChatDraftStatus(chatId, hasUnsavedChanges);
        }
    }, [chatId, hasUnsavedChanges, updateChatDraftStatus]);

    // Clear pending file when chat changes to prevent sending to wrong chat
    useEffect(() => {
        return () => {
            if (pendingFile) setPendingFile(null);
        };
    }, [activeChat]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) handleFileUpload(files[0]);
    };

    // ── Scheduler: Sync date/time inputs with scheduledAt prop ─────────────────
    useEffect(() => {
        if (showScheduler && scheduledAt) {
            try {
                const date = new Date(scheduledAt);
                // Check if valid date
                if (!isNaN(date.getTime())) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    setScheduledDate(`${year}-${month}-${day}`);
                    setScheduledTime(`${hours}:${minutes}`);
                }
            } catch (e) {
                console.warn("Invalid scheduledAt format:", scheduledAt);
            }
        } else if (!showScheduler) {
            // Reset when closing
            // Don't clear scheduledDate/Time here; they'll be reset by the next open if needed
        }
    }, [showScheduler, scheduledAt]);

    const handleScheduleChange = useCallback(() => {
        if (scheduledDate && scheduledTime) {
            // Create ISO string in local time (without timezone conversion issues)
            const dateTime = new Date(`${scheduledDate}T${scheduledTime}`);
            if (!isNaN(dateTime.getTime())) {
                setScheduled(dateTime.toISOString());
            }
        } else {
            setScheduled(null);
        }
    }, [scheduledDate, scheduledTime, setScheduled]);

    // Call handleScheduleChange when date or time changes
    useEffect(() => {
        handleScheduleChange();
    }, [scheduledDate, scheduledTime, handleScheduleChange]);

    // ─────────────────────────────────────────────────────────────────────
    return (
        <div className="flex-shrink-0 px-3 md:px-6 pb-4 md:pb-6 pt-2">

            {/* LINK MODAL */}
            {showLinkModal && (
                <LinkModal
                    selectedText={selectedText}
                    onInsert={handleLinkInsert}
                    onClose={() => setShowLinkModal(false)}
                />
            )}

            {/* SCHEDULER OVERLAY */}
            {showScheduler && (
                <div className="mb-3 p-3 md:p-4 rounded-2xl bg-app-surface border border-primary/20 animate-fade-in flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-premium">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <AppIcon name="ClockIcon" size={18} className="text-primary" />
                        </div>
                        <div>
                            <p className="text-[11px] font-bold text-text-primary uppercase tracking-widest">Schedule Message</p>
                            <p className="text-[10px] text-text-muted font-bold opacity-60 hidden sm:block">Will be sent automatically at the chosen time</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <input
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                            className="flex-1 sm:flex-none bg-app-bg border border-border-subtle rounded-xl px-2 md:px-3 py-2 text-[12px] text-text-primary outline-none focus:border-primary/50 shadow-subtle"
                        />
                        <input
                            type="time"
                            value={scheduledTime}
                            onChange={(e) => setScheduledTime(e.target.value)}
                            className="flex-1 sm:flex-none bg-app-bg border border-border-subtle rounded-xl px-2 md:px-3 py-2 text-[12px] text-text-primary outline-none focus:border-primary/50 shadow-subtle"
                        />
                        <button
                            onClick={() => setShowScheduler(false)}
                            className="p-2 text-text-muted hover:text-text-primary transition-colors flex-shrink-0"
                        >
                            <AppIcon name="XMarkIcon" size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* MAIN INPUT CONTAINER */}
            <div
                className={`relative flex flex-col rounded-[24px] border transition-all duration-300 shadow-premium ${dragOver
                    ? "border-primary bg-primary/5 ring-4 ring-primary/10 scale-[1.01]"
                    : focused
                        ? "bg-app-surface border-primary ring-4 ring-primary/5"
                        : "bg-app-surface border-border-subtle"
                    }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
            >
                {/* TOOLBAR */}
                <div className="flex items-center gap-0.5 md:gap-1 px-2 pt-2 pb-1 border-b border-border-subtle/50 overflow-x-auto">
                    <ToolbarButton icon="CodeBracketIcon" active={isCode} onClick={() => setIsCode(!isCode)} title="Code block" />
                    <div className="w-px h-4 bg-border-subtle mx-0.5 md:mx-1 flex-shrink-0" />

                    {/* Bold */}
                    <button
                        onClick={handleBold}
                        title="Bold"
                        className="p-1.5 h-8 w-8 flex items-center justify-center rounded-lg text-text-muted hover:bg-black/5 hover:text-text-primary font-bold transition-all flex-shrink-0"
                    >
                        B
                    </button>

                    {/* Italic */}
                    <button
                        onClick={handleItalic}
                        title="Italic"
                        className="p-1.5 h-8 w-8 flex items-center justify-center rounded-lg text-text-muted hover:bg-black/5 hover:text-text-primary italic transition-all flex-shrink-0 hidden sm:flex"
                    >
                        I
                    </button>

                    {/* Link */}
                    <ToolbarButton icon="LinkIcon" onClick={handleLinkClick} title="Insert link" className="hidden sm:flex" />

                    {/* Bullet List */}
                    <ToolbarButton icon="ListBulletIcon" onClick={handleBulletList} title="Bullet list" className="hidden sm:flex" />

                    <div className="w-px h-4 bg-white/10 mx-0.5 md:mx-1 flex-shrink-0 hidden sm:block" />

                    {/* Attachment */}
                    <ToolbarButton
                        icon="PaperClipIcon"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className={isUploading ? "animate-pulse" : ""}
                        title="Attach file"
                    />

                    {/* Emoji Picker */}
                    <div className="relative flex-shrink-0">
                        <ToolbarButton
                            ref={emojiBtnRef}
                            icon="FaceSmileIcon"
                            active={showEmojiPicker}
                            onClick={() => setShowEmojiPicker((p) => !p)}
                            title="Emoji picker"
                            aria-label="Open emoji picker"
                        />
                        {showEmojiPicker && (
                            <EmojiPicker
                                anchorRef={emojiBtnRef}
                                pickerRef={emojiPickerRef}
                                onSelect={handleEmojiSelect}
                                onClose={() => setShowEmojiPicker(false)}
                            />
                        )}
                    </div>

                    {/* @ Mention */}
                    <ToolbarButton icon="AtSymbolIcon" onClick={handleAtClick} title="Mention someone" className="hidden sm:flex" />
                </div>

                {/* MENTION DROPDOWN */}
                {mentionQuery !== null && channelMembers.length > 0 && (
                    <div className="relative">
                        <MentionDropdown
                            query={mentionQuery}
                            members={channelMembers}
                            onSelect={handleMentionSelect}
                            dropdownRef={mentionDropdownRef}
                        />
                    </div>
                )}

                {/* FILE PREVIEW */}
                {pendingFile && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-app-bg border border-border-subtle rounded-2xl mb-2 mx-4 mt-2 shadow-subtle animate-fade-in group/pending relative">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold">
                            <AppIcon name={getFileIcon(pendingFile.name)} size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-bold text-text-primary truncate">{pendingFile.name}</p>
                            <p className="text-[10px] text-text-muted font-bold uppercase tracking-widest">
                                {(pendingFile.size / 1024).toFixed(1)} KB • Ready to send
                            </p>
                        </div>
                        <button
                            onClick={handleCancelFile}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-app-surface border border-border-subtle rounded-full flex items-center justify-center text-text-muted hover:text-error shadow-subtle transition-all"
                            title="Remove file"
                        >
                            <AppIcon name="XMarkIcon" size={14} />
                        </button>
                    </div>
                )}

                {/* TEXTAREA */}
                <textarea
                    ref={inputRef}
                    value={value}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={
                        isCode
                            ? "Paste your code snippet here..."
                            : `Message ${channelName ? `#${channelName}` : "conversation"}...`
                    }
                    className={`w-full bg-transparent px-4 py-4 text-[15px] text-text-primary resize-none outline-none placeholder:text-text-muted custom-scrollbar ${isCode ? "font-mono bg-black/5" : ""}`}
                    rows={Math.min(10, value.split("\n").length || 1)}
                    disabled={isUploading}
                />

                {/* DRAFT INDICATOR */}
                <DraftIndicator
                    lastSaved={lastSaved}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isLoading={draftLoading}
                />

                {/* BOTTOM ACTIONS */}
                <div className="flex items-center justify-between px-3 pb-3 pt-1">
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setShowScheduler(!showScheduler)}
                            className={`flex items-center gap-2 px-2 md:px-3 py-2 rounded-xl text-[11px] font-bold uppercase tracking-widest transition-all ${showScheduler
                                ? "bg-primary text-white shadow-premium"
                                : "text-text-muted hover:bg-black/5 hover:text-text-primary"
                                }`}
                        >
                            <AppIcon name="CalendarDaysIcon" size={14} />
                            <span className="hidden sm:inline">Schedule</span>
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        {isUploading && (
                            <span className="text-[10px] text-primary animate-pulse font-bold uppercase tracking-widest mr-1 hidden sm:inline">
                                Uploading...
                            </span>
                        )}
                        <button
                            onClick={() => handleSend()}
                            disabled={!(value.trim() || pendingFile) || isUploading || isScheduling}
                            className={`h-9 md:h-10 px-4 md:px-6 rounded-xl text-[13px] font-bold flex items-center gap-2 transition-all transform active:scale-95 shadow-premium ${(value.trim() || pendingFile) && !isUploading && !isScheduling
                                ? "bg-primary text-white shadow-primary/20 hover:bg-primary-dark"
                                : "bg-black/5 text-text-muted cursor-not-allowed"
                                }`}
                        >
                            <span className="hidden sm:inline">Send</span>
                            <AppIcon
                                name="PaperAirplaneIcon"
                                size={14}
                                className={(value.trim() || pendingFile) ? "translate-x-0.5 -translate-y-0.5" : "opacity-40"}
                            />
                        </button>
                    </div>
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
            />
        </div>
    );
}

// ── Shared ToolbarButton ──────────────────────────────────────────────────────
const ToolbarButton = React.forwardRef(function ToolbarButton(
    { icon, onClick, active, disabled, className = "", title, "aria-label": ariaLabel },
    ref
) {
    return (
        <button
            ref={ref}
            onClick={onClick}
            disabled={disabled}
            title={title}
            aria-label={ariaLabel || title}
            className={`p-1.5 h-8 w-8 flex items-center justify-center rounded-lg transition-all duration-200 ${active
                ? "bg-primary/10 text-primary"
                : "text-text-muted hover:bg-bg-muted hover:text-text-primary"
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
        >
            <AppIcon name={icon} size={15} />
        </button>
    );
});